import { ColumnFilter, ILookupService, ObjectTyped } from '@price-for-profit/micro-services';
import { CLIENT_ID, CUSTOMER_PRODUCT_TABLE, PRODUCT_TABLE } from 'shared/constants/app';
import { CustomerProductPricesDTO } from 'shared/types/customerManagement';
import { CustomerProductLookup, ProductFilter, ProductLookup, ProductViewGridItem } from 'shared/types/product';
import { browserDownloadCsv, formatDate, getWeekExportStartEnd } from 'shared/utility';
import { unparse } from 'papaparse';

export interface IProductService {
  getProductsWithFilters: (filters: ProductFilter) => Promise<ProductViewGridItem[]>;
  getCustomerProductsByCustomer: (customerName: string) => Promise<CustomerProductPricesDTO[]>;
  downloadProductViewProducts: (filters: ProductFilter) => Promise<boolean>;
}

export class ProductService {
  private shared: { clientId: string } = { clientId: CLIENT_ID };
  constructor(private lookupService: ILookupService) {}

  async getProductsWithFilters(filters: ProductFilter) {
    const productFilterMap: { [key in keyof ProductFilter]?: keyof ProductLookup } = {
      abgBrand: 'brand',
      channel: 'channel',
      pricingMethod: 'price_type',
      //masterCustomer: 'abgmastercustomer',
      // add a new column mapping if a new filter is implemented
    };
    const columnFilters: ColumnFilter<ProductLookup>[] = [];
    for (let key in productFilterMap) {
      // @ts-ignore -- tight timeline
      if (filters[key]) {
        columnFilters.push({
          // @ts-ignore -- tight timeline and confident the keys and values are valid due to above typing
          columnName: productFilterMap[key],
          dataType: 'string',
          queryOperator: 'contains',
          // @ts-ignore -- tight timeline and confident the keys and values are valid due to above typing
          value: filters[key],
        });
      }
    }

    const products = await this.lookupService.filteredAndSearch<ProductLookup>({
      clientId: this.shared.clientId,
      table: PRODUCT_TABLE,
      columnFilters,
    });
    return products.map(
      (product): ProductViewGridItem => ({
        id: product.uniqueid,
        productCode: product.sku,
        description: product.description,
        brand: product.brand,
        shape: product.shape,
        finish: product.finish,
        dimensions: product.width, // TODO rlittlejohn: figure out how to display dimensions correctly
        lastTwelveMonthRevenue: parseFloat(product.product_l12m_sales),
        asp: parseFloat(product.asp),
        listPrice: parseFloat(product.current_price),
        recommendedPrice: 0,
        revTier: product.product_tier,
        status: '',
      })
    );
  }

  async getCustomerProductsByCustomer(customerName: string) {
    try {
      const customerProducts = await this.lookupService.filteredAndSearch<CustomerProductLookup>({
        clientId: this.shared.clientId,
        table: CUSTOMER_PRODUCT_TABLE,
        columnFilters: [
          { columnName: 'abgmastercustomer', dataType: 'string', queryOperator: 'eq', value: customerName },
        ],
      });

      return customerProducts.map(
        (customerProduct): CustomerProductPricesDTO => ({
          id: customerProduct.uniqueid,
          customerName: customerProduct.abgmastercustomer,
          channel: customerProduct.channel,
          revTier: '', // TODO rlittlejohn
          pricingMethod: customerProduct.pricing_method,
          listPrice: parseFloat(customerProduct.list_price),
          recommendedPrice: 0, // TODO rlittlejohn
          lastTwelveMonthRevenue: parseFloat(customerProduct.product_l12m_sales),
          lastTwelveMonthQuantity: parseInt(customerProduct.product_l12m_qty),
          estimatedImpact: 0, // TODO rlittlejohn
          status: '', // TODO rlittlejohn
          brand: customerProduct.brand,
          sku: customerProduct.sku,
          productDescription: customerProduct.description,
          productDescription2: customerProduct.description_2,
          shape: customerProduct.shape,
          finish: customerProduct.finish,
          currentPrice: parseFloat(customerProduct.current_price),
          oldPrice: parseFloat(customerProduct.old_price),
          effectiveDate: customerProduct.effective_date,
        })
      );
    } catch (err) {
      console.log({ err });
      return [];
    }
  }

  async downloadProductViewProducts(filters: ProductFilter) {
    const today = new Date();
    const data = await this.getProductsWithFilters(filters);
    const [start, end] = getWeekExportStartEnd(today);
    const fileName = `products-${formatDate(start)}-${formatDate(end)}.csv`;

    if (!data || (data && !data.length)) {
      return false;
    }

    const fields = ObjectTyped.keys(data[0]);
    const csvData = data.map(record => ObjectTyped.values(record));

    const csv = unparse({ data: csvData, fields });

    browserDownloadCsv(csv, fileName);

    return true;
  }
}
